import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { toast } from 'react-toastify';
import api from '../../services/api';

const TRELLO_API_KEY = 'fd26e58838f7e042b1dc0ba74785fe4c';
const TRELLO_CALLBACK = `${window.location.origin}/trello/callback`;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 700,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#172b4d',
    marginBottom: theme.spacing(1),
  },
  connectBtn: {
    background: '#0052cc',
    color: '#fff',
    borderRadius: 8,
    textTransform: 'none',
    '&:hover': { background: '#0041a8' },
  },
  disconnectBtn: {
    borderRadius: 8,
    textTransform: 'none',
    color: '#dc2626',
    borderColor: '#dc2626',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: theme.spacing(2),
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  select: {
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
    minWidth: 240,
    marginBottom: theme.spacing(2),
  },
  tableCell: {
    padding: '8px 12px',
    fontSize: '0.85rem',
  },
  saveBtn: {
    borderRadius: 8,
    textTransform: 'none',
    marginTop: theme.spacing(2),
  },
}));

const TrelloIntegration = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [boardLists, setBoardLists] = useState([]);
  const [laneMapping, setLaneMapping] = useState({});
  const [onClose, setOnClose] = useState('archive');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingTickets, setSyncingTickets] = useState(false);
  const [syncingMeta, setSyncingMeta] = useState(false);
  const [lanes, setLanes] = useState([{ id: 'lane0', title: 'Em aberto' }]);

  useEffect(() => {
    api.get('/tag/kanban/').then(({ data }) => {
      const tagLanes = (data.lista || []).map(t => ({ id: String(t.id), title: t.name }));
      setLanes([{ id: 'lane0', title: 'Em aberto' }, ...tagLanes]);
    }).catch(() => {});
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/trello/config');
      setConfig(data);
      if (data.connected) {
        setSelectedBoard(data.boardId || '');
        setLaneMapping(data.laneMapping || {});
        setOnClose(data.onClose || 'archive');
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (config?.connected) {
      api.get('/trello/boards').then(({ data }) => setBoards(data)).catch(() => {});
    }
  }, [config]);

  useEffect(() => {
    if (selectedBoard) {
      api.get(`/trello/boards/${selectedBoard}/lists`)
        .then(({ data }) => setBoardLists(data))
        .catch(() => {});
    }
  }, [selectedBoard]);

  const handleConnect = () => {
    const url =
      `https://trello.com/1/authorize` +
      `?key=${TRELLO_API_KEY}` +
      `&name=Ethimos` +
      `&expiration=never` +
      `&scope=read,write` +
      `&response_type=token` +
      `&return_url=${encodeURIComponent(TRELLO_CALLBACK)}`;

    const popup = window.open(url, 'trello_oauth', 'width=600,height=600');

    const onMessage = async event => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'TRELLO_TOKEN') return;

      window.removeEventListener('message', onMessage);
      if (popup) popup.close();

      try {
        await api.post('/trello/connect', { token: event.data.token });
        toast.success('Conta Trello conectada com sucesso!');
        loadConfig();
      } catch {
        toast.error('Erro ao salvar token do Trello');
      }
    };

    window.addEventListener('message', onMessage);

    // Clean up if popup is closed without completing
    const interval = setInterval(() => {
      if (popup?.closed) {
        clearInterval(interval);
        window.removeEventListener('message', onMessage);
      }
    }, 500);
  };

  const handleDisconnect = async () => {
    try {
      await api.delete('/trello/disconnect');
      toast.success('Conta Trello desconectada');
      setConfig({ connected: false });
      setBoards([]);
      setSelectedBoard('');
      setBoardLists([]);
      setLaneMapping({});
    } catch {
      toast.error('Erro ao desconectar');
    }
  };

  const handleAutoSync = async () => {
    if (!selectedBoard) {
      toast.warning('Selecione um board Trello primeiro');
      return;
    }
    setSyncing(true);
    try {
      const { data } = await api.post('/trello/sync-lanes');
      setLaneMapping(data.laneMapping || {});
      toast.success('Lanes sincronizadas automaticamente com o Trello!');
      loadConfig();
    } catch {
      toast.error('Erro ao sincronizar lanes');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncTickets = async () => {
    setSyncingTickets(true);
    try {
      const { data } = await api.post('/trello/sync-tickets');
      toast.success(`${data.created} cards criados no Trello (${data.skipped} já existiam)`);
    } catch {
      toast.error('Erro ao sincronizar tickets');
    } finally {
      setSyncingTickets(false);
    }
  };

  const handleSyncMeta = async () => {
    setSyncingMeta(true);
    try {
      const { data } = await api.post('/trello/sync-cards-meta');
      toast.success(`${data.updated} cards atualizados com nome e informações do cliente`);
    } catch {
      toast.error('Erro ao atualizar informações dos cards');
    } finally {
      setSyncingMeta(false);
    }
  };

  const handleSave = async () => {
    if (!selectedBoard) {
      toast.warning('Selecione um board Trello');
      return;
    }
    setSaving(true);
    try {
      const board = boards.find(b => b.id === selectedBoard);
      await api.post('/trello/config/save', {
        boardId: selectedBoard,
        boardName: board?.name || '',
        laneMapping,
        onClose,
      });
      toast.success('Configuração Trello salva e ativada!');
      loadConfig();
    } catch {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <div className={classes.root}>
      {/* Connection status */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Integração com Trello
        </Typography>

        <div className={classes.status}>
          <span
            className={classes.dot}
            style={{ background: config?.connected ? '#16a34a' : '#94a3b8' }}
          />
          <Typography variant="body2">
            {config?.connected ? 'Conta conectada' : 'Não conectado'}
          </Typography>
        </div>

        {!config?.connected ? (
          <Button
            variant="contained"
            className={classes.connectBtn}
            onClick={handleConnect}
          >
            Conectar com Trello
          </Button>
        ) : (
          <Button
            variant="outlined"
            className={classes.disconnectBtn}
            startIcon={<LinkOffIcon />}
            onClick={handleDisconnect}
          >
            Desconectar
          </Button>
        )}
      </div>

      {config?.connected && (
        <>
          <Divider style={{ marginBottom: 24 }} />

          {/* Board selection */}
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Board</Typography>
            <FormControl variant="outlined" size="small" className={classes.select}>
              <InputLabel>Selecionar board...</InputLabel>
              <Select
                value={selectedBoard}
                onChange={e => setSelectedBoard(e.target.value)}
                label="Selecionar board..."
              >
                {boards.map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Botões de sync automático */}
          {selectedBoard && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AutorenewIcon />}
                onClick={handleAutoSync}
                disabled={syncing}
                style={{ borderRadius: 8, textTransform: 'none' }}
              >
                {syncing ? 'Sincronizando...' : 'Sincronizar Lanes Automaticamente'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleSyncTickets}
                disabled={syncingTickets}
                style={{ borderRadius: 8, textTransform: 'none' }}
              >
                {syncingTickets ? 'Enviando...' : 'Enviar Tickets Existentes ao Trello'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSyncMeta}
                disabled={syncingMeta}
                style={{ borderRadius: 8, textTransform: 'none' }}
              >
                {syncingMeta ? 'Atualizando...' : 'Atualizar Informações dos Cards'}
              </Button>
            </div>
          )}

          {/* Lane mapping */}
          {selectedBoard && boardLists.length > 0 && (
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Mapeamento de Lanes → Listas Trello
              </Typography>
              <Paper variant="outlined" style={{ borderRadius: 8 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        <strong>Lane Ethimos</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <strong>Lista Trello</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lanes.map(lane => (
                      <TableRow key={lane.id}>
                        <TableCell className={classes.tableCell}>{lane.title}</TableCell>
                        <TableCell className={classes.tableCell}>
                          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                            <Select
                              value={laneMapping[lane.id.toString()] || ''}
                              onChange={e =>
                                setLaneMapping(prev => ({
                                  ...prev,
                                  [lane.id.toString()]: e.target.value,
                                }))
                              }
                              displayEmpty
                            >
                              <MenuItem value="">Não sincronizar</MenuItem>
                              {boardLists.map(list => (
                                <MenuItem key={list.id} value={list.id}>
                                  {list.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </div>
          )}

          {/* On close action */}
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Ao fechar ticket</Typography>
            <RadioGroup
              value={onClose}
              onChange={e => setOnClose(e.target.value)}
              row
            >
              <FormControlLabel
                value="archive"
                control={<Radio color="primary" />}
                label="Arquivar card"
              />
              <FormControlLabel
                value="move"
                control={<Radio color="primary" />}
                label='Mover para lista "Fechados"'
              />
            </RadioGroup>
          </div>

          <Button
            variant="contained"
            color="primary"
            className={classes.saveBtn}
            startIcon={<SyncIcon />}
            onClick={handleSave}
            disabled={saving || !selectedBoard}
          >
            {saving ? 'Salvando...' : 'Salvar e Ativar'}
          </Button>

          {config?.isActive && (
            <Typography
              variant="caption"
              style={{ display: 'block', marginTop: 8, color: '#16a34a' }}
            >
              ✓ Sync ativo — mover cards no Kanban reflete no Trello
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default TrelloIntegration;
